import { Country, countryData, PhoneField } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from '@/intl'
import { formatPhoneNumberInput } from '@/scopes/common/components/FormElements/PhoneFormatter'
import { useStatus } from '@/scopes/common/components/FormElements/useStatus'
import { getErrorMessage } from './getErrorMessage'

export interface PhoneInputProps {
  name: string
  label?: string
  patternErrorMessage?: string | ((selectedCountry: Country | undefined) => string)
  required?: boolean
  requiredErrorMessage?: string
  countries: Country[]
  formFieldClass?: string
  helpText?: string
  disabled?: boolean
  /** Enable when the field is rendered inside a Sheet, so the country list owns the scroll while open */
  modal?: boolean
}

interface PhoneInputFieldValue {
  countryCode: string
  phoneNumber: string
}

export function PhoneInput({
  name,
  label,
  required,
  requiredErrorMessage,
  patternErrorMessage,
  countries = countryData,
  formFieldClass,
  helpText,
  disabled,
  modal,
}: PhoneInputProps) {
  const intl = useIntl()
  const value = useWatch({ name })
  const defaultCountryCode: string | undefined = value?.countryCode
  const defaultCountry: Country = countries.find(({ code }) => code === defaultCountryCode) || countries[0]
  const [selectedCountryMobile, setSelectedCountryMobile] = React.useState(defaultCountry)

  const {
    formState,
    control,
    trigger,
    setValue,
  } = useFormContext()
  const { errors } = formState
  const validations = {
    ...(required
      ? {
          required: (fieldValue: PhoneInputFieldValue) => Boolean(fieldValue?.phoneNumber?.trim()),
        }
      : {}),
    ...(selectedCountryMobile.pattern
      ? {
          matchPattern: (fieldValue: PhoneInputFieldValue) =>
            validatePattern(fieldValue?.phoneNumber, selectedCountryMobile.pattern),
        }
      : {}),
  }
  const {
    field: { onChange, onBlur, ...inputProps },
  } = useController({
    name,
    control,
    disabled,
    rules: {
      validate: validations,
    },
  })

  const status = useStatus(
    {
      name,
      required,
      validations,
      shouldMarkValidStatus: (fieldValue?: PhoneInputFieldValue) => Boolean(fieldValue?.phoneNumber?.trim()),
    },
  )
  const resolvedPatternErrorMessage = typeof patternErrorMessage === 'function'
    ? patternErrorMessage(selectedCountryMobile)
    : patternErrorMessage

  const errorMessage = getErrorMessage(
    name,
    errors,
    { patternErrorMessage: resolvedPatternErrorMessage, requiredErrorMessage },
  )

  React.useEffect(() => {
    const phone = value?.phoneNumber?.trim()
    if (phone) {
      void trigger(name)
    }
  }, [name, selectedCountryMobile, trigger, value?.phoneNumber])

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const valueOnBlur = event.target.value
    const formatted = formatPhoneNumberInput(valueOnBlur, selectedCountryMobile.code, selectedCountryMobile.isoCode.toLowerCase())
    const { prefix, phoneNumber } = formatted

    if (phoneNumber) {
      setValue(name, {
        countryCode: prefix || selectedCountryMobile.code,
        phoneNumber,
      }, { shouldValidate: true })
      setSelectedCountryMobile(countries.find(({ code }) => code === prefix) || selectedCountryMobile)
    }
    else {
      setValue(name, '', { shouldValidate: true })
    }

    onBlur && onBlur()
  }

  const handleCountryChange = (country: Country) => {
    setSelectedCountryMobile(country)
  }

  return (
    <PhoneField
      countries={countries}
      label={label}
      feedbackText={errorMessage}
      status={status}
      inputProps={{
        ...inputProps,
        value: value?.phoneNumber,
        disabled,
      }}
      defaultCountryCode={defaultCountryCode}
      countryCode={selectedCountryMobile.code}
      value={value?.phoneNumber}
      onPhoneChange={onChange}
      onBlur={handleBlur}
      onCountryChange={handleCountryChange}
      formFieldClass={formFieldClass}
      optionalLabel={!required ? intl.formatMessage({ id: 'common_form_field_optional_label' }) : undefined}
      helpText={helpText}
      name={name}
      $disabled={disabled}
      modal={modal}
    />
  )
}

function validatePattern(value?: string, pattern?: string) {
  if (!value || !pattern) {
    return true
  }
  return new RegExp(pattern).test(value)
}
